import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { authRoutingDebugLines, emptyAuthRoutingSnapshot, inspectAuthRouting, type AuthRoutingSnapshot } from "@/lib/auth-routing";

export const Route = createFileRoute("/_authenticated/app/")({
  ssr: false,
  head: () => ({ meta: [{ title: "Portal" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: AppLanding,
});

function AppLanding() {
  const navigate = useNavigate();
  const [debug, setDebug] = useState<AuthRoutingSnapshot>(() => emptyAuthRoutingSnapshot());
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      if (!cancelled) setTimedOut(true);
    }, 5_000);

    inspectAuthRouting().then((snapshot) => {
      if (cancelled) return;
      setDebug(snapshot);
      if (!snapshot.sessionPresent || snapshot.error || !snapshot.targetRoute) {
        window.clearTimeout(timeout);
        setTimedOut(true);
        return;
      }
      const nextSnapshot = { ...snapshot, lastRedirectAttempt: snapshot.targetRoute };
      setDebug(nextSnapshot);
      navigate({ to: snapshot.targetRoute, replace: true }).catch((error) => {
        if (cancelled) return;
        setDebug({
          ...nextSnapshot,
          error: error instanceof Error ? error.message : JSON.stringify(error),
        });
        setTimedOut(true);
      });
    });

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 text-muted-foreground">
      <div className="w-full max-w-lg rounded border bg-card p-4 text-sm shadow-sm">
        <div className="font-medium text-foreground">
          {timedOut ? "Auth-/Routing-Fehler" : "Berechtigungen werden geprüft …"}
        </div>
        {timedOut && (
          <p className="mt-2 text-destructive">
            Der Zielwechsel wurde nicht abgeschlossen. Debugdaten:
          </p>
        )}
        <div className="mt-4 space-y-1 text-xs">
          {authRoutingDebugLines(debug).map(([label, value]) => (
            <div key={label} className="flex justify-between gap-3">
              <span>{label}</span>
              <span className="max-w-64 truncate text-right font-mono text-foreground">{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
